import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ETAPAS = [
  { value: 'nuevo',      label: 'Nuevo',      color: '#6b7280' },
  { value: 'contactado', label: 'Contactado',  color: '#3b82f6' },
  { value: 'interesado', label: 'Interesado',  color: '#f59e0b' },
  { value: 'propuesta',  label: 'Propuesta',   color: '#8b5cf6' },
  { value: 'ganado',     label: 'Ganado',      color: '#10b981' },
  { value: 'perdido',    label: 'Perdido',     color: '#ef4444' },
];

const TIPOS_SEGUIMIENTO = [
  { value: 'llamada',  label: '📞 Llamada' },
  { value: 'whatsapp', label: '💬 WhatsApp' },
  { value: 'email',    label: '✉️ Email' },
  { value: 'reunion',  label: '🤝 Reunión' },
  { value: 'otro',     label: '📝 Otro' },
];

const inputStyle = {
  border: '2px solid rgba(59, 130, 246, 0.3)',
  borderRadius: '8px',
  padding: '0.75rem',
  fontSize: '1rem',
  backgroundColor: '#fafafa',
  width: '100%',
  transition: 'all 0.2s',
};

const focusStyle = {
  borderColor: 'rgba(59, 130, 246, 0.6)',
  backgroundColor: '#ffffff',
  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  outline: 'none',
};

const labelStyle = { fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block' };

const FichaProspecto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [prospecto, setProspecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const [formData, setFormData] = useState({
    nombres: '', apellidos: '', identificacion: '', telefono: '', whatsapp_number: '',
    email: '', empresa: '', cargo: '', intereses: '', modelo: '',
    sector: '', direccion: '', ciudad: '', etapa: 'nuevo', notas: '',
  });

  const [seguimientoForm, setSeguimientoForm] = useState({
    tipo: 'llamada', descripcion: '', resultado: '', fecha_proximo: '',
  });

  const esAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

  const fetchProspecto = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/crm/prospectos/${id}`);
      setProspecto(data);
      setFormData({
        nombres: data.nombres || '', apellidos: data.apellidos || '',
        identificacion: data.identificacion || '', telefono: data.telefono || '',
        whatsapp_number: data.whatsapp_number || '',
        email: data.email || '', empresa: data.empresa || '',
        cargo: data.cargo || '', intereses: data.intereses || '',
        modelo: data.modelo || '', sector: data.sector || '',
        direccion: data.direccion || '', ciudad: data.ciudad || '',
        etapa: data.etapa || 'nuevo', notas: data.notas || '',
      });
    } catch {
      toast.error('Error al cargar el prospecto');
      navigate('/crm/prospectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProspecto(); }, [id]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/crm/prospectos/${id}`, formData);
      toast.success('Prospecto actualizado');
      setShowEditModal(false);
      fetchProspecto();
    } catch { toast.error('Error al guardar'); }
  };

  const handleSeguimiento = async (e) => {
    e.preventDefault();
    if (!seguimientoForm.descripcion.trim()) { toast.error('La descripción es requerida'); return; }
    try {
      await api.post('/crm/seguimientos', {
        prospecto_id: id,
        ...seguimientoForm,
        fecha_proximo: seguimientoForm.fecha_proximo || null,
      });
      toast.success('Seguimiento registrado');
      setShowSeguimientoModal(false);
      setSeguimientoForm({ tipo: 'llamada', descripcion: '', resultado: '', fecha_proximo: '' });
      fetchProspecto();
    } catch { toast.error('Error al registrar seguimiento'); }
  };

  const handleBloquear = () => {
    if (!prospecto.whatsapp_number) { toast.error('Este prospecto no tiene número WhatsApp'); return; }
    setConfirmAction({
      mensaje: `¿Agregar ${prospecto.whatsapp_number} a la lista negra? No recibirás más mensajes de este número en el CRM.`,
      onConfirm: async () => {
        try {
          await api.post('/crm/blacklist', {
            whatsapp_number: prospecto.whatsapp_number,
            razon: `Bloqueado desde ficha — ${prospecto.nombres} ${prospecto.apellidos || ''}`.trim(),
            prospecto_id: id,
          });
          toast.success('Número bloqueado');
          navigate('/crm/prospectos');
        } catch { toast.error('Error al bloquear'); }
      },
    });
  };

  const handleClasificar = () => {
    setConfirmAction({
      mensaje: '¿Marcar como cliente? Un cliente no puede volver a ser prospecto.',
      onConfirm: async () => {
        try {
          await api.patch(`/crm/prospectos/${id}/clasificar`, { es_cliente: true });
          toast.success('Marcado como cliente');
          fetchProspecto();
        } catch { toast.error('Error al clasificar'); }
      },
    });
  };

  const iStyle = (field) => ({ ...inputStyle, ...(focusedField === field ? focusStyle : {}) });

  if (loading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading" />
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!prospecto) return null;

  const etapaInfo = ETAPAS.find(e => e.value === prospecto.etapa) || ETAPAS[0];

  return (
    <Layout>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/crm/prospectos')} style={{ fontSize: '1.2rem', padding: '0.4rem 0.8rem' }}>
            ←
          </button>
          <div>
            <h2>🎯 {prospecto.nombres} {prospecto.apellidos || ''}</h2>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{
                background: etapaInfo.color + '20', color: etapaInfo.color,
                border: `1px solid ${etapaInfo.color}40`, borderRadius: '12px',
                padding: '0.2rem 0.7rem', fontSize: '0.82rem', fontWeight: '600',
              }}>
                {etapaInfo.label}
              </span>
              <span style={{
                background: prospecto.es_cliente ? '#d1fae5' : '#eff6ff',
                color: prospecto.es_cliente ? '#065f46' : '#1d4ed8',
                borderRadius: '12px', padding: '0.2rem 0.7rem',
                fontSize: '0.82rem', fontWeight: '600',
              }}>
                {prospecto.es_cliente ? '✅ Cliente' : '🎯 Prospecto'}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>✏️ Editar</button>
          {!prospecto.es_cliente && (
            <button
              className="btn"
              style={{
                background: '#d1fae5',
                color: '#065f46',
                border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer',
              }}
              onClick={handleClasificar}
            >
              ✅ Marcar como cliente
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowSeguimientoModal(true)}>
            + Seguimiento
          </button>
          {prospecto.whatsapp_number && (
            <button
              onClick={handleBloquear}
              title="Bloquear número en lista negra"
              style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '8px', padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              🚫 Bloquear
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Ficha izquierda */}
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>📋 Información de Contacto</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { label: 'Cédula / RUC', val: prospecto.identificacion },
                { label: 'Teléfono',     val: prospecto.telefono },
                { label: 'WhatsApp',     val: prospecto.whatsapp_number },
                { label: 'Email',        val: prospecto.email },
                { label: 'Dirección',    val: prospecto.direccion },
                { label: 'Ciudad',       val: prospecto.ciudad },
                { label: 'Empresa',      val: prospecto.empresa },
                { label: 'Cargo',        val: prospecto.cargo },
                { label: 'Sector',       val: prospecto.sector },
                { label: 'Modelo',       val: prospecto.modelo },
                { label: 'Asignado a',   val: prospecto.asignado_nombre?.trim() || null },
              ].map(({ label, val }) => (
                <div key={label} style={{ background: 'var(--background)', borderRadius: '8px', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{label}</div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                    {val || <span style={{ color: '#9ca3af' }}>—</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(prospecto.intereses || prospecto.notas) && (
            <div className="card">
              {prospecto.intereses && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>💡 Intereses</div>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{prospecto.intereses}</p>
                </div>
              )}
              {prospecto.notas && (
                <div>
                  <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>📝 Notas</div>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{prospecto.notas}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Seguimientos derecha */}
        <div>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--text-primary)' }}>📅 Seguimientos ({(prospecto.seguimientos || []).length})</h3>
              <button className="btn btn-sm btn-primary" onClick={() => setShowSeguimientoModal(true)}>+ Agregar</button>
            </div>

            {!prospecto.seguimientos?.length ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
                <p>Sin seguimientos aún</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflowY: 'auto' }}>
                {prospecto.seguimientos.map(s => {
                  const tipoInfo = TIPOS_SEGUIMIENTO.find(t => t.value === s.tipo) || TIPOS_SEGUIMIENTO[4];
                  return (
                    <div key={s.id} style={{
                      background: 'var(--background)', borderRadius: '10px', padding: '1rem',
                      borderLeft: '3px solid #3b82f6',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.85rem' }}>{tipoInfo.label}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {new Date(s.fecha_contacto).toLocaleString('es-EC', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: s.resultado ? '0.4rem' : 0, whiteSpace: 'pre-wrap' }}>
                        {s.descripcion}
                      </p>
                      {s.resultado && (
                        <p style={{ color: '#059669', fontSize: '0.85rem', fontStyle: 'italic' }}>
                          ↳ {s.resultado}
                        </p>
                      )}
                      {s.fecha_proximo && (
                        <p style={{ fontSize: '0.78rem', color: '#7c3aed', marginTop: '0.3rem' }}>
                          📅 Próximo: {new Date(s.fecha_proximo).toLocaleDateString('es-EC')}
                        </p>
                      )}
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>
                        por {s.usuario_nombre?.trim()}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal editar */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  🤝
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Editar Prospecto
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Modifica la información del prospecto
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleGuardar}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Datos personales */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',        label: 'Nombres',      required: true, placeholder: 'Ej. Juan Carlos' },
                      { key: 'apellidos',       label: 'Apellidos',                   placeholder: 'Ej. Pérez López' },
                      { key: 'identificacion',  label: 'Cédula / RUC',                placeholder: '1234567890' },
                      { key: 'telefono',        label: 'Teléfono',                    placeholder: '0999 999 999' },
                      { key: 'whatsapp_number', label: 'WhatsApp',                    placeholder: '0999 999 999' },
                      { key: 'email',           label: 'Email',        type: 'email', placeholder: 'correo@ejemplo.com' },
                      { key: 'direccion',       label: 'Dirección',                   placeholder: 'Calle, número, sector' },
                      { key: 'ciudad',          label: 'Ciudad',                      placeholder: 'Ej. Quito' },
                    ].map(({ key, label, required, type, placeholder }) => (
                      <div key={key} className="form-group" style={key === 'email' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input
                          type={type || 'text'}
                          className="form-input"
                          value={formData[key]}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          required={!!required}
                          placeholder={placeholder}
                          style={iStyle(key)}
                          onFocus={() => setFocusedField(key)}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Información empresarial */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>🏢 Información empresarial</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'empresa', label: 'Empresa',           placeholder: 'Nombre de la empresa' },
                      { key: 'cargo',   label: 'Cargo',             placeholder: 'Ej. Gerente General' },
                      { key: 'sector',  label: 'Sector',            placeholder: 'Ej. Salud, Educación...' },
                      { key: 'modelo',  label: 'Modelo de negocio', placeholder: 'Ej. B2B, Retail...' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key} className="form-group">
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>{label}</label>
                        <input type="text" className="form-input" value={formData[key]}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          placeholder={placeholder} style={iStyle(key)}
                          onFocus={() => setFocusedField(key)} onBlur={() => setFocusedField(null)} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Estado del seguimiento */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#fdf4ff', color: '#7c3aed', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Estado del seguimiento</span>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Estado actual</label>
                    <select className="form-input" value={formData.etapa}
                      onChange={(e) => setFormData({ ...formData, etapa: e.target.value })}
                      style={iStyle('etapa')} onFocus={() => setFocusedField('etapa')} onBlur={() => setFocusedField(null)}>
                      {ETAPAS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Intereses</label>
                    <textarea className="form-input" value={formData.intereses}
                      onChange={(e) => setFormData({ ...formData, intereses: e.target.value })}
                      rows={2} placeholder="¿Qué productos o servicios le interesan?"
                      style={iStyle('intereses')} onFocus={() => setFocusedField('intereses')} onBlur={() => setFocusedField(null)} />
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Notas</label>
                    <textarea className="form-input" value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                      rows={2} placeholder="Observaciones adicionales..."
                      style={iStyle('notas')} onFocus={() => setFocusedField('notas')} onBlur={() => setFocusedField(null)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal seguimiento */}
      {showSeguimientoModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  🤝
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Registrar Seguimiento
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos para agregar un nuevo seguimiento
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSeguimientoModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSeguimiento}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>Tipo de contacto</label>
                  <select
                    className="form-input"
                    value={seguimientoForm.tipo}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, tipo: e.target.value })}
                    style={iStyle('tipo_seg')}
                    onFocus={() => setFocusedField('tipo_seg')} onBlur={() => setFocusedField(null)}
                  >
                    {TIPOS_SEGUIMIENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>Descripción *</label>
                  <textarea
                    className="form-input"
                    value={seguimientoForm.descripcion}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, descripcion: e.target.value })}
                    rows={3}
                    required
                    placeholder="¿Qué se conversó o realizó?"
                    style={iStyle('desc_seg')}
                    onFocus={() => setFocusedField('desc_seg')} onBlur={() => setFocusedField(null)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>Resultado</label>
                  <input
                    type="text"
                    className="form-input"
                    value={seguimientoForm.resultado}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, resultado: e.target.value })}
                    placeholder="¿Cuál fue el resultado o acuerdo?"
                    style={iStyle('result_seg')}
                    onFocus={() => setFocusedField('result_seg')} onBlur={() => setFocusedField(null)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>Próximo seguimiento</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={seguimientoForm.fecha_proximo}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha_proximo: e.target.value })}
                    style={iStyle('proximo_seg')}
                    onFocus={() => setFocusedField('proximo_seg')} onBlur={() => setFocusedField(null)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSeguimientoModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmar */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>{confirmAction.mensaje}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FichaProspecto;
